import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import JsonLd from "@/components/base/JsonLd";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackTop from "@/components/feature/BackTop";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories, news } from "@/mocks/content";
import { ListRow, MiniCard } from "@/components/feature/Cards";
import NewsletterForm from "@/components/feature/NewsletterForm";

type SortKey = "newest" | "hot" | "likes";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const cat = categories.find((c) => c.slug === slug);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("newest");

  const all = useMemo(
    () => news.filter((n) => n.category === slug),
    [slug],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? all.filter(
          (n) =>
            n.title.toLowerCase().includes(q) ||
            n.lead.toLowerCase().includes(q) ||
            n.tags.some((t) => t.toLowerCase().includes(q)),
        )
      : all;
    const sorted = [...base];
    if (sort === "hot") sorted.sort((a, b) => b.views - a.views);
    if (sort === "likes") sorted.sort((a, b) => b.likes - a.likes);
    return sorted;
  }, [all, query, sort]);

  const related = news
    .filter((n) => n.category === slug && n.id !== (filtered[0]?.id ?? -1))
    .slice(0, 5);

  const valid = Boolean(cat);
  usePageMeta(
    valid
      ? `${cat.name} - ${cat.tagline} | 今日新瓜独家栏目`
      : "栏目不存在 | 今日新瓜",
    valid
      ? `${cat.intro} 看今日新瓜「${cat.name}」栏目的独家内幕、独家爆料与深度内容，深挖事件背后不为人知的真相。`
      : "您访问的栏目不存在，欢迎回到今日新瓜首页探索独家内容。",
  );

  if (!valid) {
    return (
      <>
        <Navbar />
        <main className="mx-auto flex min-h-[60vh] max-w-3xl flex-col items-center justify-center px-4 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-3xl text-secondary-600">
            <i className="ri-ghost-line" />
          </span>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-900">
            这个栏目被吃掉了
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            找不到「{slug}」对应栏目，请从首页选择你要的独家内容。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-600 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-700"
          >
            返回首页
          </Link>
        </main>
        <Footer />
        <BackTop />
      </>
    );
  }

  const schemaCollection = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${cat.name} | 今日新瓜`,
    description: cat.intro,
    inLanguage: "zh-CN",
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "/" },
        { "@type": "ListItem", position: 2, name: cat.name, item: `/category/${cat.slug}` },
      ],
    },
  };

  return (
    <>
      <JsonLd data={schemaCollection} />
      <Navbar />
      <main>
        {/* 面包屑 */}
        <div className="mx-auto max-w-7xl px-4 pt-6 md:px-6">
          <nav
            aria-label="面包屑"
            className="flex items-center gap-1.5 text-sm text-foreground-500"
          >
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link
              to={`/category/${cat.slug}`}
              className="font-medium text-primary-600"
            >
              {cat.name}
            </Link>
          </nav>
        </div>

        {/* 栏目头 */}
        <header className="mx-auto max-w-7xl px-4 pb-6 pt-4 md:px-6">
          <div className="flex flex-col gap-5 rounded-2xl border border-background-200 bg-background-50 p-6 md:flex-row md:items-center md:p-8">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-primary-600 text-3xl text-background-50">
              <i className={cat.icon} />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">
                  {cat.name}
                </h1>
                <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-700">
                  共 {all.length} 篇独家内容
                </span>
              </div>
              <p className="mt-1 text-sm font-medium text-primary-600">
                {cat.tagline}
              </p>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                {cat.intro}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2 md:flex-col">
              <Link
                to="/about"
                className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-mail-send-line" /> 投稿爆料
              </Link>
            </div>
          </div>

          {/* 栏目快捷切换 */}
          <div className="mt-4 flex flex-wrap gap-2">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                  c.slug === cat.slug
                    ? "bg-foreground-950 text-background-50"
                    : "border border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                <i className={c.icon} />
                {c.name}
              </Link>
            ))}
          </div>
        </header>

        <div className="mx-auto grid max-w-7xl gap-8 px-4 pb-14 md:px-6 lg:grid-cols-[1fr_320px]">
          {/* 列表区 */}
          <section>
            <div className="mb-5 flex flex-col gap-3 rounded-xl border border-background-200 bg-background-50 p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-1 items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-2 focus-within:border-primary-400">
                <i className="ri-search-line text-foreground-500" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={`在「${cat.name}」中搜索……`}
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
              </div>
              <div className="flex shrink-0 gap-1 rounded-full bg-background-100 p-1">
                {(
                  [
                    { k: "newest", label: "最新" },
                    { k: "hot", label: "最热" },
                    { k: "likes", label: "最多赞" },
                  ] as { k: SortKey; label: string }[]
                ).map((s) => (
                  <button
                    key={s.k}
                    onClick={() => setSort(s.k)}
                    className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                      sort === s.k
                        ? "bg-primary-600 text-background-50"
                        : "text-foreground-600 hover:text-primary-700"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-50 py-20 text-center">
                <i className="ri-inbox-2-line text-4xl text-foreground-300" />
                <p className="mt-3 text-sm text-foreground-600">
                  没有找到与“{query}”相关的内容，换个关键词试试。
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((item) => (
                  <ListRow key={item.id} item={item} />
                ))}
              </div>
            )}
          </section>

          {/* 侧栏 */}
          <aside className="space-y-6">
            <div className="rounded-2xl border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
                <i className="ri-fire-line text-primary-600" />
                本栏热读
              </h2>
              <div className="mt-3">
                {related.map((item) => (
                  <MiniCard key={item.id} item={item} />
                ))}
              </div>
            </div>
            <div className="rounded-2xl bg-primary-700 p-6 text-center text-background-50">
              <h2 className="flex items-center justify-center gap-2 font-heading text-lg font-bold">
                <i className="ri-mail-fill text-accent-300" />
                订阅 {cat.name}
              </h2>
              <p className="mt-2 text-sm text-background-100/90">
                该栏目有独家更新，第一时间推送给你。
              </p>
              <div className="mt-4">
                <NewsletterForm />
              </div>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
      <BackTop />
    </>
  );
}